import React, { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@area-restrita/client';
import {
  getGetActingDetailQueryKey,
  useAbortActing,
} from '@area-restrita/api-client/endpoints/actings/actings.gen';
import { toast } from 'react-toastify';
import { BanIcon } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useAuthSessionStore } from '@/modules/authentication/stores/authSessionStore';
import { formatActingDateTime } from '@/modules/actings/pages/ActingDetails/actingDetailsPresentation';

interface IProps {
  acting: {
    id: string;
    status: string;
    startedAt: string;
    startedBy: { name: string | null };
    busline: { code: string };
  };
}

const AbortActingAction: React.FC<IProps> = ({ acting }) => {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { checkAuthorization } = useAuthSessionStore();
  const abortActing = useAbortActing();
  const canAbort = checkAuthorization(({ can }) => can('abort', 'actings'));
  const isStarted = acting.status === 'started';

  const refreshActings = useCallback(
    async () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['/api/v1/actings'] }),
        queryClient.invalidateQueries({
          queryKey: getGetActingDetailQueryKey(acting.id),
        }),
      ]),
    [acting.id, queryClient],
  );

  const handleConfirm = useCallback(async () => {
    if (abortActing.isPending || !isStarted) return;

    try {
      await abortActing.mutateAsync({ id: acting.id });
      await refreshActings();
      setOpen(false);
      toast.success('Execução abortada com sucesso.');
    } catch (error) {
      if (error instanceof ApiError && error.code === 'ACTING_NOT_STARTED') {
        await refreshActings();
        setOpen(false);
        toast.error(
          'Esta execução já foi encerrada. Os dados foram atualizados.',
        );
        return;
      }

      toast.error('Não foi possível abortar a execução. Tente novamente.');
    }
  }, [abortActing, acting.id, isStarted, refreshActings]);

  if (!canAbort) return null;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!abortActing.isPending) setOpen(nextOpen);
      }}
    >
      <AlertDialogTrigger asChild>
        <Button
          variant="destructive"
          size="icon-sm"
          disabled={!isStarted}
          aria-label="Abortar execução"
          title="Abortar execução"
          onClick={(event) => event.stopPropagation()}
        >
          <BanIcon />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Abortar execução?</AlertDialogTitle>
          <AlertDialogDescription>
            A execução da linha {acting.busline.code}, conduzida por{' '}
            {acting.startedBy.name ?? 'motorista não identificado'}, iniciada em{' '}
            {formatActingDateTime(acting.startedAt)}, será encerrada e deixará
            de aceitar novos dados.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={abortActing.isPending}>
            Voltar
          </AlertDialogCancel>
          <Button
            variant="destructive"
            loading={abortActing.isPending}
            onClick={handleConfirm}
          >
            Abortar execução
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default AbortActingAction;
